import React from "react";
import { Box, MapPin, PackagePlus, Sparkles, Trash2, Users, X } from "lucide-react";
import LazyImage from "./LazyImage";
import { SURFACE, REF_LIMIT } from "./constants";
import HoverImagePreview from "./HoverImagePreview";

const TYPE_ICON = {
  characters: Users,
  scenes: MapPin,
  props: Box,
};

/**
 * 左栏 —— 已选参考图展示 + 打开弹窗入口
 *
 * Props:
 *  - selectedRefs     { characters: [...], scenes: [...], props: [...] }  已解析为 named refs
 *  - onRemoveRef(key)  移除某张（key = `${type}:${assetId}`）
 *  - onOpenPicker      打开 AssetPickerModal
 *  - onMatchFromScript 按剧本匹配（可选）
 *  - onClear           一键清空（可选）
 *  - disabled
 *  - limit             最多可选数（默认 16）
 */
const SelectedRefsPanel = ({
  selectedRefs,
  onRemoveRef,
  onOpenPicker,
  onMatchFromScript,
  onClear,
  disabled,
  limit = REF_LIMIT,
  className = "",
  embedded = false,
}) => {
  const all = [
    ...(selectedRefs?.characters || []).map((r) => ({ ...r, _type: "characters" })),
    ...(selectedRefs?.scenes || []).map((r) => ({ ...r, _type: "scenes" })),
    ...(selectedRefs?.props || []).map((r) => ({ ...r, _type: "props" })),
  ];
  const total = all.length;
  const reachLimit = total >= limit;

  const sectionTitleStyle = {
    color: "var(--color-text-muted)",
  };

  return (
    <aside
      className={`min-h-0 ${embedded ? "border rounded-lg overflow-hidden" : "border-r"} flex flex-col ${className}`}
      style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.panel }}
    >
      {/* Header */}
      <div
        className="shrink-0 p-3 border-b space-y-2"
        style={{ borderColor: "var(--color-border-primary)" }}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>
              本集参考图
            </div>
            <div className="text-xs mt-0.5" style={sectionTitleStyle}>
              已选 {total} / {limit} 张
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onOpenPicker}
          disabled={disabled}
          className="w-full h-10 rounded-md border text-sm font-bold inline-flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          style={{
            borderColor: reachLimit ? "var(--color-warning)" : "var(--color-accent-primary)",
            backgroundColor: reachLimit ? SURFACE.card : "var(--color-accent-primary)",
            color: reachLimit ? "var(--color-warning)" : "var(--color-accent-contrast)",
          }}
        >
          <PackagePlus size={15} />
          {total === 0 ? "添加参考图" : reachLimit ? "已达上限，管理参考图" : "管理参考图"}
        </button>
        <div className="grid grid-cols-2 gap-2">
          {onMatchFromScript && (
            <button
              type="button"
              onClick={onMatchFromScript}
              disabled={disabled}
              className="h-8 rounded-md border text-xs font-semibold inline-flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
              style={{
                borderColor: "var(--color-border-primary)",
                backgroundColor: SURFACE.card,
                color: "var(--color-text-secondary)",
              }}
            >
              <Sparkles size={12} />
              按剧本匹配
            </button>
          )}
          {onClear && total > 0 && (
            <button
              type="button"
              onClick={onClear}
              disabled={disabled}
              className="h-8 rounded-md border text-xs font-semibold inline-flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
              style={{
                borderColor: "var(--color-border-primary)",
                backgroundColor: SURFACE.card,
                color: "var(--color-error)",
              }}
            >
              <Trash2 size={12} />
              清空
            </button>
          )}
        </div>
      </div>

      {/* Selected list */}
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-3 space-y-3" style={{ backgroundColor: SURFACE.soft }}>
        {total === 0 ? (
          <div
            className="h-full min-h-[240px] rounded-md border border-dashed flex flex-col items-center justify-center text-xs text-center px-4 py-8"
            style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: SURFACE.panel }}
          >
            <PackagePlus size={22} style={{ color: "var(--color-text-muted)", opacity: 0.6 }} />
            <div className="mt-2 font-semibold" style={{ color: "var(--color-text-secondary)" }}>
              还没有选参考图
            </div>
            <div className="mt-1 leading-5">
              点击上方按钮，从角色 / 场景 / 道具库里选本集要保持连续的参考图。
            </div>
          </div>
        ) : (
          ["characters", "scenes", "props"].map((type) => {
            const list = (selectedRefs?.[type] || []);
            if (!list.length) return null;
            const Icon = TYPE_ICON[type] || PackagePlus;
            const label = type === "characters" ? "人物" : type === "scenes" ? "场景" : "道具";
            return (
              <section key={type}>
                <div
                  className="flex items-center gap-1.5 text-xs font-semibold mb-1.5"
                  style={{ color: "var(--color-text-secondary)" }}
                >
                  <Icon size={12} />
                  {label}
                  <span
                    className="text-[10px] font-mono px-1 rounded"
                    style={{ backgroundColor: SURFACE.card, color: "var(--color-text-muted)" }}
                  >
                    {list.length}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {list.map((ref) => (
                    <div
                      key={ref.id}
                      className="relative rounded-md border overflow-hidden"
                      style={{ borderColor: "var(--color-border-primary)", backgroundColor: SURFACE.card }}
                    >
                      <HoverImagePreview src={ref.base64} title={ref.name}>
                        <LazyImage
                          src={ref.base64}
                          alt={ref.name}
                          aspectClass="aspect-square"
                        />
                      </HoverImagePreview>
                      <button
                        type="button"
                        onClick={() => onRemoveRef?.(ref.id)}
                        disabled={disabled}
                        className="absolute top-1 right-1 w-6 h-6 rounded-md border inline-flex items-center justify-center cursor-pointer disabled:opacity-50"
                        style={{
                          borderColor: "var(--color-border-primary)",
                          backgroundColor: "color-mix(in srgb, var(--color-bg-card) 88%, transparent)",
                          color: "var(--color-error)",
                        }}
                        aria-label="移除"
                      >
                        <X size={12} />
                      </button>
                      <div
                        className="px-1.5 py-1 text-[11px] font-semibold truncate"
                        style={{ color: "var(--color-text-primary)" }}
                        title={ref.name}
                      >
                        {(ref.name || "").replace(/^(人物|场景|道具)：/, "")}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })
        )}
      </div>

      <div
        className="shrink-0 p-3 border-t text-[11px] leading-5"
        style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-muted)", backgroundColor: SURFACE.card }}
      >
        这里只显示本集会使用的参考图。导演只会用你选中的图，不会自动加图。
      </div>
    </aside>
  );
};

export default SelectedRefsPanel;
